import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";

const DEFAULT_STAGES = [
  "Reading your answers...",
  "Understanding your market...",
  "Benchmarking competitors...",
  "Estimating costs & revenue...",
  "Scoring launch readiness...",
  "Compiling your report...",
];

export function AIProcessing({ title, subtitle, stages = DEFAULT_STAGES, testid = "ai-processing" }) {
  const [stageIdx, setStageIdx] = useState(0);
  const [elapsed, setElapsed] = useState(0);

  useEffect(() => {
    const stageTimer = setInterval(() => setStageIdx((i) => (i + 1) % stages.length), 3500);
    const clock = setInterval(() => setElapsed((s) => s + 1), 1000);
    return () => {
      clearInterval(stageTimer);
      clearInterval(clock);
    };
  }, [stages.length]);

  return (
    <div className="glass rounded-2xl p-12 sm:p-14 text-center" data-testid={testid}>
      <div className="relative w-32 h-32 mx-auto mb-8" aria-hidden="true">
        <motion.div
          className="absolute inset-0 rounded-full border border-[#FF8800]/30"
          animate={{ rotate: 360 }}
          transition={{ duration: 8, repeat: Infinity, ease: "linear" }}
        >
          <span className="absolute -top-1.5 left-1/2 w-3 h-3 -ml-1.5 rounded-full bg-[#FF8800] shadow-[0_0_14px_#FF8800]" />
        </motion.div>
        <motion.div
          className="absolute inset-3 rounded-full border border-[#00B0FF]/30"
          animate={{ rotate: -360 }}
          transition={{ duration: 6, repeat: Infinity, ease: "linear" }}
        >
          <span className="absolute -top-1 left-1/2 w-2 h-2 -ml-1 rounded-full bg-[#00B0FF] shadow-[0_0_12px_#00B0FF]" />
        </motion.div>
        <motion.div
          className="absolute inset-6 rounded-full border border-[#00E676]/30"
          animate={{ rotate: 360 }}
          transition={{ duration: 4.5, repeat: Infinity, ease: "linear" }}
        >
          <span className="absolute -top-1 left-1/2 w-2 h-2 -ml-1 rounded-full bg-[#00E676] shadow-[0_0_12px_#00E676]" />
        </motion.div>
        <motion.div
          className="absolute inset-0 m-auto w-10 h-10 rounded-full bg-gradient-to-br from-[#FF8800] to-[#ff5e00]"
          animate={{ scale: [1, 1.18, 1], opacity: [0.85, 1, 0.85] }}
          transition={{ duration: 1.8, repeat: Infinity, ease: "easeInOut" }}
        />
      </div>

      <p className="font-display text-lg font-bold text-white">{title}</p>
      {subtitle && <p className="text-xs text-slate-500 mt-1">{subtitle}</p>}

      <div className="h-6 mt-4">
        <AnimatePresence mode="wait">
          <motion.p
            key={stageIdx}
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -8 }}
            transition={{ duration: 0.35 }}
            className="text-sm text-[#00B0FF] font-mono-tech"
            data-testid={`${testid}-stage`}
          >
            {stages[stageIdx]}
          </motion.p>
        </AnimatePresence>
      </div>

      <div className="mt-5 h-1 max-w-xs mx-auto rounded-full bg-white/5 overflow-hidden">
        <motion.div
          className="h-full rounded-full bg-gradient-to-r from-[#FF8800] via-[#00B0FF] to-[#00E676]"
          animate={{ x: ["-100%", "100%"] }}
          transition={{ duration: 1.6, repeat: Infinity, ease: "easeInOut" }}
          style={{ width: "45%" }}
        />
      </div>

      <p className="mt-4 text-[11px] font-mono-tech text-slate-500" data-testid={`${testid}-elapsed`}>
        {elapsed}s elapsed — usually done in under a minute
      </p>
    </div>
  );
}
